'use client';

import { c as _c } from "react/compiler-runtime";
import './child-bio-image.scss';
import { useTranslation } from 'react-i18next';
import { HtmlBlock } from '@sola/ark-core/atoms/html/html.tsx';
import { ChildImageVariantEnum, getChildImageUrlBase } from '@sola/ark-core/utils/child-image.ts';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import type { ChildAvailabilityItem, ChildDetails } from '@sola/schema/dotcom/children/child.ts';
import loader from '@sola/shared-utils/ark-skippy/loader.ts';
import type { ImageSizeIntent } from '../../atoms/image/image.tsx';
import { Image } from '../../atoms/image/image.tsx';
import { useSacImageAltText } from '../../utils/alt-text.ts';
import placeholder from './placeholder.png';
type ChildBioImageProps = {
  availability: ChildAvailabilityItem;
  details: ChildDetails;
  aspectRatio?: string;
  aspectRatioSm?: string;
  aspectRatioMd?: string;
  aspectRatioLg?: string;
  childImageVariant?: ChildImageVariantEnum;
  caption?: string;
  centerCaption?: boolean;
  /**
   * Drives the `sizes` attribute. Defaults to `third`, matching the bio layout
   * where the media column is `flex: 1` against a `flex: 2` content column.
   */
  sizeIntent?: ImageSizeIntent;
  fullWidth?: boolean;
};
export const ChildBioImage = t0 => {
  const $ = _c(45);
  const {
    details,
    availability,
    aspectRatio: t1,
    aspectRatioSm,
    aspectRatioMd,
    aspectRatioLg,
    childImageVariant: t2,
    caption,
    centerCaption: t3,
    sizeIntent: t4,
    fullWidth: t5
  } = t0;
  const aspectRatio = t1 === undefined ? "3-4" : t1;
  const childImageVariant = t2 === undefined ? ChildImageVariantEnum.Full : t2;
  const centerCaption = t3 === undefined ? false : t3;
  const sizeIntent = t4 === undefined ? "third" : t4;
  const fullWidth = t5 === undefined ? false : t5;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = ["bio", "children", "geo"];
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  const {
    t
  } = useTranslation(t6);
  const {
    imageUrl,
    name,
    hangul
  } = details;
  const {
    gender,
    isSpecialNeeds,
    isOrphan,
    isInHivAffectedArea,
    minDaysWaiting,
    icp: t7
  } = availability;
  const {
    country
  } = t7;
  let t8;
  if ($[1] !== childImageVariant || $[2] !== imageUrl) {
    t8 = getChildImageUrlBase(imageUrl, childImageVariant);
    $[1] = childImageVariant;
    $[2] = imageUrl;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  const childImage = t8;
  const titleAndAlt = useSacImageAltText(gender, country, hangul, minDaysWaiting, name.preferred, isOrphan, isSpecialNeeds, isInHivAffectedArea);
  let t9;
  if ($[4] !== hangul.preferred || $[5] !== minDaysWaiting || $[6] !== name.preferred || $[7] !== t) {
    t9 = t("bio:waitingCaption_short", {
      childName: name.preferred,
      koreanName: hangul.preferred,
      days: minDaysWaiting
    });
    $[4] = hangul.preferred;
    $[5] = minDaysWaiting;
    $[6] = name.preferred;
    $[7] = t;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const captionShort = t9;
  let t10;
  if ($[9] !== hangul.preferred || $[10] !== minDaysWaiting || $[11] !== name.preferred || $[12] !== t) {
    t10 = t("bio:waitingCaption", {
      childName: name.preferred,
      koreanName: hangul.preferred,
      days: minDaysWaiting
    });
    $[9] = hangul.preferred;
    $[10] = minDaysWaiting;
    $[11] = name.preferred;
    $[12] = t;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const captionFull = t10;
  const captionText = caption ?? (fullWidth ? captionShort : captionFull);
  let t11;
  if ($[14] !== fullWidth) {
    t11 = cn("ChildBioImage xl:pr-comfy-xl", {
      "ChildBioImage--fullWidth": fullWidth
    });
    $[14] = fullWidth;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const t12 = childImage || placeholder;
  let t13;
  if ($[16] !== t12 || $[17] !== titleAndAlt) {
    t13 = {
      url: t12,
      alt: titleAndAlt
    };
    $[16] = t12;
    $[17] = titleAndAlt;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== aspectRatio || $[20] !== aspectRatioLg || $[21] !== aspectRatioMd || $[22] !== aspectRatioSm || $[23] !== childImage || $[24] !== sizeIntent || $[25] !== t13) {
    t14 = <Image key={childImage} image={t13} loader={loader} sizeIntent={sizeIntent} lazyLoad="eager" aspectRatio={aspectRatio} aspectRatioSm={aspectRatioSm} aspectRatioMd={aspectRatioMd} aspectRatioLg={aspectRatioLg} className="ChildBioImage__Figure__img" />;
    $[19] = aspectRatio;
    $[20] = aspectRatioLg;
    $[21] = aspectRatioMd;
    $[22] = aspectRatioSm;
    $[23] = childImage;
    $[24] = sizeIntent;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== centerCaption || $[28] !== fullWidth) {
    t15 = cn("ChildBioImage__Figure__figcaption label label-xl", {
      "text-center": centerCaption,
      "text-center sm:text-left": fullWidth
    });
    $[27] = centerCaption;
    $[28] = fullWidth;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== caption || $[31] !== captionFull || $[32] !== captionShort || $[33] !== captionText || $[34] !== fullWidth) {
    t16 = fullWidth && !caption ? <><div className="sm:hidden"><HtmlBlock htmlString={captionShort} /></div><div className="hidden sm:block"><HtmlBlock htmlString={captionFull} /></div></> : <HtmlBlock htmlString={captionText} />;
    $[30] = caption;
    $[31] = captionFull;
    $[32] = captionShort;
    $[33] = captionText;
    $[34] = fullWidth;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t15 || $[37] !== t16) {
    t17 = <figcaption className={t15}>{t16}</figcaption>;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t14 || $[40] !== t17) {
    t18 = <figure className="ChildBioImage__Figure">{t14}{t17}</figure>;
    $[39] = t14;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t11 || $[43] !== t18) {
    t19 = <div className={t11}>{t18}</div>;
    $[42] = t11;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  return t19;
};